/**
 * ToolConfirmationCard Component
 * Displays human-in-the-loop (HITL) action confirmation cards in chat bubbles.
 */

import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Wrench,
  X,
} from 'lucide-react';
import { ChatActionConfirmation } from '../../types';

interface ToolConfirmationCardProps {
  confirmation: ChatActionConfirmation;
  onConfirm?: (confirmation: ChatActionConfirmation) => Promise<void> | void;
  onCancel?: (confirmation: ChatActionConfirmation) => Promise<void> | void;
  isUser?: boolean;
}

export const ToolConfirmationCard: React.FC<ToolConfirmationCardProps> = ({
  confirmation,
  onConfirm,
  onCancel,
}) => {
  const [status, setStatus] = useState<ChatActionConfirmation['status']>(confirmation.status);
  const [showDetails, setShowDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (confirmation.status && confirmation.status !== status) {
      setStatus(confirmation.status);
    }
  }, [confirmation.status]);

  const isSensitive = confirmation.riskLevel === 'sensitive';
  const isWrite = confirmation.riskLevel === 'write' || isSensitive;
  const isPending = status === 'pending';
  const isExecuting = status === 'executing';

  const handleConfirmClick = async () => {
    if (!isPending || isExecuting) return;
    setStatus('executing');
    setErrorMessage(null);

    try {
      if (onConfirm) {
        await onConfirm(confirmation);
      }
      setStatus('confirmed');
    } catch (err) {
      console.error('Confirmation action failed:', err);
      setStatus('failed');
      setErrorMessage(err instanceof Error ? err.message : 'Thao tác không thành công');
    }
  };

  const handleCancelClick = async () => {
    if (!isPending || isExecuting) return;
    setStatus('cancelled');

    try {
      if (onCancel) {
        await onCancel(confirmation);
      }
    } catch (err) {
      console.error('Cancel action error:', err);
    }
  };

  return (
    <div
      className={`mt-3 rounded-xl border p-4 shadow-sm transition-all ${
        isSensitive
          ? 'border-red-300 bg-red-50/70 dark:border-red-900/60 dark:bg-red-950/20'
          : isWrite
            ? 'border-amber-300 bg-amber-50/60 dark:border-amber-800/50 dark:bg-amber-950/20'
            : 'border-blue-200 bg-blue-50/50 dark:border-slate-700 dark:bg-slate-800/40'
      }`}
    >
      {/* Header with Risk Badge & Tool Name */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200/80 pb-3 dark:border-slate-700/80">
        <div className="flex items-center gap-2">
          {isSensitive ? (
            <ShieldAlert className="text-red-600 dark:text-red-400" size={18} />
          ) : (
            <Wrench className="text-amber-600 dark:text-amber-400" size={18} />
          )}
          <span className="font-semibold text-gray-900 dark:text-slate-100">
            {confirmation.actionTitle || confirmation.toolName}
          </span>
          <span className="text-xs text-gray-500 dark:text-slate-400">
            ({confirmation.toolName})
          </span>
        </div>

        {/* Risk Badge */}
        <div className="flex items-center gap-1.5">
          {isSensitive ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
              <ShieldAlert size={12} />
              Rủi ro cao (Sensitive)
            </span>
          ) : isWrite ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              <AlertTriangle size={12} />
              Thay đổi dữ liệu (Write)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
              <ShieldCheck size={12} />
              Chỉ đọc (Read)
            </span>
          )}
        </div>
      </div>

      {/* Summary message */}
      <div className="py-3 text-sm leading-relaxed text-gray-800 dark:text-slate-200">
        {confirmation.summary ||
          'Agent yêu cầu bạn xác nhận trước khi thực thi hành động này với hệ thống bên ngoài.'}
      </div>

      {/* Sensitive Security Warning */}
      {isSensitive && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-100/60 p-2.5 text-xs text-red-800 dark:border-red-800/40 dark:bg-red-950/40 dark:text-red-300">
          <strong>Lưu ý an toàn:</strong> Thao tác này có tính chất nhạy cảm hoặc không thể hoàn
          tác. Vui lòng kiểm tra kỹ các thông số trước khi phê duyệt.
        </div>
      )}

      {/* Parameters Collapsible Preview */}
      {confirmation.parameters && Object.keys(confirmation.parameters).length > 0 && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200"
          >
            {showDetails ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Chi tiết tham số ({Object.keys(confirmation.parameters).length})</span>
          </button>

          {showDetails && (
            <div className="mt-2 space-y-1.5 rounded-lg border border-gray-200/80 bg-white/70 p-3 text-xs dark:border-slate-700 dark:bg-slate-900/60">
              {Object.entries(confirmation.parameters).map(([key, value]) => (
                <div key={key} className="flex flex-col sm:flex-row sm:items-baseline gap-1">
                  <span className="font-semibold text-gray-700 dark:text-slate-300 sm:w-1/3 truncate">
                    {key}:
                  </span>
                  <span className="font-mono text-gray-900 dark:text-slate-100 sm:w-2/3 break-all">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              ))}
              {confirmation.endpointUrl && (
                <div className="pt-2 border-t border-gray-100 dark:border-slate-800 flex items-center gap-1 text-gray-500 dark:text-slate-400">
                  <ExternalLink size={12} />
                  <span className="truncate">{confirmation.endpointUrl}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Message if failed */}
      {errorMessage && (
        <div className="mb-3 rounded-md bg-red-100 p-2 text-xs text-red-800 dark:bg-red-900/40 dark:text-red-300">
          {errorMessage}
        </div>
      )}

      {/* Status & Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        {/* Status Indicator */}
        <div className="flex items-center gap-2 text-xs font-medium">
          {status === 'pending' && (
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
              <Clock size={14} />
              Đang chờ bạn xác nhận
            </span>
          )}
          {status === 'executing' && (
            <span className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
              <Loader2 size={14} className="animate-spin" />
              Đang gửi yêu cầu đến server...
            </span>
          )}
          {status === 'confirmed' && (
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
              <Check size={14} />
              Đã xác nhận & thực thi thành công
            </span>
          )}
          {status === 'cancelled' && (
            <span className="inline-flex items-center gap-1 text-gray-500 dark:text-slate-400">
              <X size={14} />
              Đã hủy hành động
            </span>
          )}
          {status === 'failed' && (
            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
              <AlertTriangle size={14} />
              Thực thi thất bại
            </span>
          )}
        </div>

        {/* Buttons (only enabled in pending status) */}
        {isPending && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancelClick}
              disabled={isExecuting}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <X size={14} />
              Hủy
            </button>
            <button
              type="button"
              onClick={handleConfirmClick}
              disabled={isExecuting}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
                isSensitive
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {isExecuting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              Xác nhận thực hiện
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
